/* Prix day-ahead Belgique : moyenne mensuelle (base, €/MWh) depuis janvier 2022.
 * Données : table day_ahead_monthly, alimentée côté serveur par la fonction day-ahead-prices (API ENTSO-E).
 * Le navigateur ne contacte jamais ENTSO-E. Options : heures à prix négatif (producteurs) et prix du contrat du membre. */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar, CartesianGrid, Cell, ComposedChart, LabelList, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { CONTRACT_FIELDS, type ContractKind, hasSupplierContract, fixedPriceOf, isBiHourly, variableFormulaOf } from './contractFields';

interface OwnPart {
  /** all : monohoraire ; hp / hc : heures pleines / heures creuses */
  part: 'all' | 'hp' | 'hc';
  fixed: number | null;
  a: number | null;
  b: number | null;
}

interface MonthRow {
  month: string;
  avg_price: number | null;
  min_price: number | null;
  max_price: number | null;
  negative_hours: number;
  quarter_hours: number;
  expected_quarter_hours: number;
  is_partial: boolean;
}

interface Props {
  /** Site affiché (contrats d'énergie : prix fixe ou formule A × X + B). */
  site: any;
  /** Contrats du site : prélèvement et / ou injection. */
  kinds: ContractKind[];
  /** Afficher l'option « heures à prix négatif » (producteurs et prosumers). */
  producerView: boolean;
}

const C = { bar: '#2F6F8F', barCurrent: '#A9C6D3', neg: '#F59B42', grid: '#E3ECEF', muted: '#51626E', border: '#C9DFE5' };
const OWN_COLOR: Record<ContractKind, string> = { offtake: '#0C1C2C', injection: '#B35F0F' };
const FIRST_MONTH = '2022-01';

export function DayAheadChart({ site, kinds, producerView }: Props) {
  const { t, i18n } = useTranslation();
  const tm = (key: string, opts?: Record<string, unknown>) => t(`memberContract.market.${key}`, opts) as string;
  const loc = i18n.language === 'nl' ? 'nl-BE' : i18n.language === 'en' ? 'en-GB' : 'fr-BE';
  const fmt = (v: number | null | undefined, d = 1) => (v === null || v === undefined ? '—' : v.toLocaleString(loc, { minimumFractionDigits: d, maximumFractionDigits: d }));

  const [rows, setRows] = useState<MonthRow[] | null>(null);
  const [index, setIndex] = useState<Record<string, Record<string, number>>>({});
  const [showNeg, setShowNeg] = useState(false);
  // Prix propres affichés par défaut (masquables avec leur bouton).
  const [showOwn, setShowOwn] = useState<Record<ContractKind, boolean>>({ offtake: true, injection: true });

  useEffect(() => {
    supabase.from('day_ahead_monthly').select('*').gte('month', FIRST_MONTH).order('month')
      .then(({ data, error }: { data: MonthRow[] | null; error: unknown }) => setRows(error ? [] : (data ?? [])));
  }, []);

  /* Contrats du site : prix fixe, ou A × X + B avec X = valeur mensuelle de l'indice (Formules), Belpex = moyenne day-ahead à défaut.
   * Bihoraire : une ligne heures pleines et une ligne heures creuses.
   * Prix de l'énergie seule, hors taxes et hors frais de réseau ; limité aux dates du contrat. */
  const num = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
  const contracts = kinds.filter(kind => hasSupplierContract(site, kind)).map(kind => {
    const F = CONTRACT_FIELDS[kind];
    const isVariable = site?.[F.type] === 'variable';
    const bi = isBiHourly(site, kind);
    const v = variableFormulaOf(site, kind);
    const parts: OwnPart[] = !bi
      ? [{ part: 'all', fixed: !isVariable ? fixedPriceOf(site, kind) : null, a: v.a, b: v.b }]
      : isVariable
        ? [{ part: 'hp', fixed: null, a: num(site[F.a1]), b: num(site[F.b1]) }, { part: 'hc', fixed: null, a: num(site[F.a2]), b: num(site[F.b2]) }]
        : [{ part: 'hp', fixed: num(site[F.hp]), a: null, b: null }, { part: 'hc', fixed: num(site[F.hc]), a: null, b: null }];
    return {
      kind, isVariable, bi, parts,
      x: (site?.[F.x] as string | null) ?? null,
      start: site?.[F.start] ? String(site[F.start]).slice(0, 7) : null,
      end: site?.[F.end] ? String(site[F.end]).slice(0, 7) : null,
    };
  }).filter(c => c.parts.every(p => (c.isVariable ? p.a !== null && p.b !== null && !!c.x : p.fixed !== null)));
  const ownKey = (kind: ContractKind, part: OwnPart['part']) => (part === 'all' ? `own_${kind}` : `own_${kind}_${part}`);
  const ownLabel = (kind: ContractKind, part: OwnPart['part']) =>
    `${tm(kind === 'offtake' ? 'ownOfftake' : 'ownInjection')}${part === 'all' ? '' : ` (${tm(part)})`}`;
  const formula = (a: number | null, x: string | null, b: number | null) =>
    tm('ownVariable', { a: fmt(a, 2), x, sign: (b ?? 0) < 0 ? '−' : '+', b: fmt(Math.abs(b ?? 0), 2) });
  // Belpex : moyenne mensuelle day-ahead de l'API ENTSO-E (barres du graphique). Autres indices : valeurs mensuelles enregistrées.
  const isBelpex = (x: string | null) => !!x && /belpex/i.test(x);
  const xs = Array.from(new Set(contracts.filter(c => c.isVariable && c.x && !isBelpex(c.x)).map(c => c.x as string))).sort();
  const xKey = xs.join('|');

  useEffect(() => {
    if (xs.length === 0) { setIndex({}); return; }
    supabase.from('indexation_parameters').select('param_name, month_key, value').in('param_name', xs)
      .then(({ data }: { data: { param_name: string; month_key: string; value: number }[] | null }) => {
        const out: Record<string, Record<string, number>> = {};
        for (const r of data ?? []) (out[r.param_name] ??= {})[r.month_key] = Number(r.value);
        setIndex(out);
      });
  }, [xKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const contractKey = JSON.stringify(contracts);
  const data = useMemo(() => {
    const current = rows?.length ? rows[rows.length - 1].month : null;
    return (rows ?? []).map(r => {
      const [y, m] = r.month.split('-').map(Number);
      const date = new Date(y, m - 1, 1);
      const own: Record<string, number | null> = {};
      for (const c of contracts) {
        const inContract = (!c.start || r.month >= c.start) && (!c.end || r.month <= c.end);
        const x = !c.isVariable ? null : isBelpex(c.x) ? r.avg_price : index[c.x as string]?.[r.month] ?? null;
        for (const p of c.parts) {
          let v: number | null = null;
          if (inContract) {
            if (c.isVariable) v = x === null || x === undefined ? null : Math.round((p.a! * Number(x) + p.b!) * 100) / 100;
            else v = p.fixed;
          }
          own[ownKey(c.kind, p.part)] = v;
        }
      }
      return {
        ...r,
        ...own,
        avg: r.avg_price === null ? null : Number(r.avg_price),
        neg: Number(r.negative_hours),
        isCurrent: r.month === current && r.is_partial,
        label: date.toLocaleDateString(loc, { month: 'short', year: '2-digit' }),
        long: date.toLocaleDateString(loc, { month: 'long', year: 'numeric' }),
      };
    });
  }, [rows, index, contractKey, loc]); // eslint-disable-line react-hooks/exhaustive-deps
  const anyOwn = contracts.some(c => showOwn[c.kind]);

  /* Moyennes annuelles pondérées par les quarts d'heure. */
  const years = useMemo(() => {
    const by = new Map<string, { sum: number; n: number; partial: boolean }>();
    for (const r of rows ?? []) {
      if (r.avg_price === null) continue;
      const y = r.month.slice(0, 4);
      const e = by.get(y) ?? { sum: 0, n: 0, partial: false };
      e.sum += Number(r.avg_price) * r.quarter_hours;
      e.n += r.quarter_hours;
      e.partial = e.partial || r.is_partial;
      by.set(y, e);
    }
    const out = Array.from(by.entries()).map(([y, e]) => ({ year: y, avg: e.n ? e.sum / e.n : null, partial: e.partial || (rows ?? []).filter(r => r.month.startsWith(y)).length < 12 }));
    return out;
  }, [rows]);

  const ticks = data.filter(d => d.month.endsWith('-01') || d.month.endsWith('-07') || d.isCurrent).map(d => d.label);

  const Tip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-[#C9DFE5] rounded-xl shadow-lg px-4 py-3 text-sm min-w-[220px]">
        <p className="font-semibold text-[#0C1C2C] capitalize">{d.long}{d.isCurrent ? ` · ${tm('current')}` : ''}</p>
        <div className="mt-2 space-y-0.5 text-[#51626E]">
          <p className="flex justify-between gap-6"><span>{tm('avg')}</span><b className="text-[#0C1C2C] tabular-nums">{fmt(d.avg)} €/MWh</b></p>
          <p className="flex justify-between gap-6"><span>{tm('minMax')}</span><span className="tabular-nums">{fmt(d.min_price, 0)} / {fmt(d.max_price, 0)}</span></p>
          {producerView && <p className="flex justify-between gap-6"><span>{tm('negHours')}</span><span className="tabular-nums">{fmt(d.neg, d.neg % 1 ? 2 : 0)} h</span></p>}
          {contracts.filter(c => showOwn[c.kind]).flatMap(c => c.parts.map(p => ({ c, p }))).filter(({ c, p }) => d[ownKey(c.kind, p.part)] !== null).map(({ c, p }) => (
            <p key={ownKey(c.kind, p.part)} className="flex justify-between gap-6"><span>{ownLabel(c.kind, p.part)}</span><b className="text-[#0C1C2C] tabular-nums">{fmt(d[ownKey(c.kind, p.part)])} €/MWh</b></p>
          ))}
          {d.is_partial && <p className="text-[11px] pt-1">{tm('partial', { n: d.quarter_hours, total: d.expected_quarter_hours })}</p>}
        </div>
      </div>
    );
  };

  const CurrentLabel = (props: any) => {
    const { x, y, width, index: i } = props;
    if (!data[i]?.isCurrent) return null;
    return <text x={x + width} y={y - 8} textAnchor="end" fontSize={11} fontWeight={600} fill={C.muted}>{tm('current')}</text>;
  };

  const chip = (on: boolean, color: string, label: string, onClick: () => void, dashed = false) => (
    <button type="button" onClick={onClick} aria-pressed={on}
      className={`inline-flex items-center gap-2 h-8 pl-2.5 pr-3 rounded-full border text-[13px] font-medium transition-colors ${on ? 'border-[#0C1C2C] text-[#0C1C2C]' : 'border-[#C9DFE5] text-[#51626E] hover:border-[#00516F]'}`}>
      {dashed
        ? <svg width="16" height="4" aria-hidden="true"><line x1="0" y1="2" x2="16" y2="2" stroke={color} strokeWidth="2" strokeDasharray={on ? '4 3' : '0'} opacity={on ? 1 : 0.5} /></svg>
        : <span className="w-3 h-3 rounded-full" style={{ background: on ? color : 'transparent', border: `1.5px solid ${color}` }} />}
      {label}
    </button>
  );

  return (
    <section className="bg-white border border-[#C9DFE5] rounded-2xl p-5 md:p-6" aria-labelledby="day-ahead-title">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 id="day-ahead-title" className="font-sans text-xl leading-tight font-bold text-[#00516F]">{tm('title')}</h2>
          <p className="text-sm text-[#51626E] mt-1">{tm('subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {producerView && chip(showNeg, C.neg, tm('negHours'), () => setShowNeg(v => !v))}
          {contracts.map(c => (
            <span key={c.kind}>{chip(showOwn[c.kind], OWN_COLOR[c.kind], tm(c.kind === 'offtake' ? 'ownOfftake' : 'ownInjection'), () => setShowOwn(v => ({ ...v, [c.kind]: !v[c.kind] })), true)}</span>
          ))}
        </div>
      </div>

      {rows === null ? (
        <div className="h-72 flex items-center justify-center text-[#8A9AA5]"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-sm text-[#51626E] text-center px-6">{tm('empty')}</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mb-3 text-[13px] text-[#51626E]">
            {years.map(y => (
              <span key={y.year}>{y.year}{y.partial ? ` (${tm('toDate')})` : ''} : <b className="text-[#0C1C2C] tabular-nums">{fmt(y.avg)} €/MWh</b></span>
            ))}
          </div>
          <div className="h-80 -mx-2 md:mx-0">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ left: 0, right: showNeg ? 4 : 12, top: 28, bottom: 0 }} barCategoryGap="18%">
                <CartesianGrid stroke={C.grid} vertical={false} />
                <XAxis dataKey="label" ticks={ticks} interval={0} tick={{ fontSize: 12, fill: C.muted }} tickLine={false} axisLine={{ stroke: C.border }} />
                <YAxis yAxisId="p" width={52} tick={{ fontSize: 12, fill: C.muted }} tickLine={false} axisLine={false}
                  tickFormatter={(v: number) => `${v}`} label={{ value: '€/MWh', angle: -90, position: 'insideLeft', offset: 12, style: { fontSize: 11, fill: C.muted } }} />
                {showNeg && (
                  <YAxis yAxisId="h" orientation="right" width={44} tick={{ fontSize: 12, fill: C.neg }} tickLine={false} axisLine={false}
                    label={{ value: 'h', angle: 0, position: 'insideTopRight', offset: 0, style: { fontSize: 11, fill: C.neg } }} />
                )}
                <Tooltip content={<Tip />} cursor={{ fill: 'rgba(12,28,44,0.04)' }} />
                <Bar yAxisId="p" dataKey="avg" name={tm('avg')} radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={false}>
                  {data.map(d => <Cell key={d.month} fill={d.isCurrent ? C.barCurrent : C.bar} />)}
                  <LabelList dataKey="avg" content={<CurrentLabel />} />
                </Bar>
                {showNeg && (
                  <Line yAxisId="h" type="monotone" dataKey="neg" name={tm('negHours')} stroke={C.neg} strokeWidth={2} dot={{ r: 2, fill: C.neg }} isAnimationActive={false} />
                )}
                {contracts.filter(c => showOwn[c.kind]).flatMap(c => c.parts.map(p => (
                  <Line key={ownKey(c.kind, p.part)} yAxisId="p" type={c.isVariable ? 'monotone' : 'stepAfter'} dataKey={ownKey(c.kind, p.part)}
                    name={ownLabel(c.kind, p.part)} stroke={OWN_COLOR[c.kind]} strokeWidth={2} strokeOpacity={p.part === 'hc' ? 0.6 : 1}
                    strokeDasharray={p.part === 'hc' ? '2 3' : '6 4'} dot={false} connectNulls={false} isAnimationActive={false} />
                )))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-[#51626E]">
            <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-[3px]" style={{ background: C.bar }} />{tm('avg')}</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-[3px]" style={{ background: C.barCurrent }} />{tm('current')}</span>
              {contracts.filter(c => showOwn[c.kind]).map(c => {
                const [p1, p2] = c.parts;
                const text = !c.bi
                  ? (c.isVariable ? formula(p1.a, c.x, p1.b) : tm('ownFixed', { price: fmt(p1.fixed, 2) }))
                  : c.isVariable
                    ? tm('ownVariableBi', { hp: formula(p1.a, c.x, p1.b), hc: formula(p2.a, c.x, p2.b) })
                    : tm('ownFixedBi', { hp: fmt(p1.fixed, 2), hc: fmt(p2.fixed, 2) });
                return (
                  <span key={c.kind} className="inline-flex items-center gap-1.5">
                    <svg width="16" height="4" aria-hidden="true"><line x1="0" y1="2" x2="16" y2="2" stroke={OWN_COLOR[c.kind]} strokeWidth="2" strokeDasharray="4 3" /></svg>
                    {tm(c.kind === 'offtake' ? 'ownOfftake' : 'ownInjection')} : {text}
                    {c.bi && <><svg width="16" height="4" aria-hidden="true" className="ml-1"><line x1="0" y1="2" x2="16" y2="2" stroke={OWN_COLOR[c.kind]} strokeOpacity="0.6" strokeWidth="2" strokeDasharray="2 3" /></svg>{tm('hc')}</>}
                  </span>
                );
              })}
            </span>
            <span>{tm('source')}</span>
          </div>
          {anyOwn && <p className="mt-2 text-xs font-medium text-[#51626E]">{tm('exclTaxes')}</p>}
        </>
      )}
    </section>
  );
}

export default DayAheadChart;
